import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, Typography, Stack, TextField, Chip, Divider, Box } from "@mui/material";
import { StepUpSpinner } from "../../../../Components/Loader";
import { paymentsApi } from "./api";

/**
 * Single "Refund / Void" action. The backend picks the operation based
 * on Worldline eligibility — the UI never promises void vs refund.
 * Partial amounts route the backend straight to refund.
 */
type Props = {
  open: boolean;
  paymentId: number;
  onClose: () => void;
  onSuccess: () => void;
};

const money = (n: any) => `$${Number(n || 0).toFixed(2)}`;

const ReversePaymentDialog: React.FC<Props> = ({ open, paymentId, onClose, onSuccess }) => {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [elig, setElig] = React.useState<any | null>(null);
  const [amount, setAmount] = React.useState<string>("");
  const [reason, setReason] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setError(null); setElig(null); setReason(""); setAmount("");
    (async () => {
      try {
        const r = await paymentsApi.reversalEligibility(paymentId);
        setElig(r);
        if (r?.remaining_refundable > 0) setAmount(String(r.remaining_refundable.toFixed(2)));
      } catch (e: any) { setError(e?.message ?? "Failed to load eligibility."); }
    })();
  }, [open, paymentId]);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const res = await paymentsApi.reverse(paymentId, {
        amount: Number(amount),
        reason,
      });
      if (res?.status === "declined") { setError(`Provider ${res.operation} declined: ${res.safe_message ?? ""}`); return; }
      if (res?.status === "requires_reconciliation") { setError("Provider outcome uncertain — reconciliation case created."); return; }
      onSuccess();
    } catch (e: any) {
      const msg = e?.message ?? "Reversal failed.";
      setError(/not configured/i.test(msg) ? "Worldline payment gateway is not configured." : msg);
    } finally { setBusy(false); }
  };

  const canSubmit = !busy && elig?.reversible && Number(amount) > 0 && Number(amount) <= (elig?.remaining_refundable ?? 0);
  const wouldTryVoid = elig && Number(amount) === (elig.original_amount ?? -1) && (elig.reversed_amount ?? 0) === 0;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm" fullWidth
      disableEscapeKeyDown={busy}
      PaperProps={{ sx: { position: "relative", overflow: "hidden" } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>Refund / Void Payment</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!elig ? <Typography>Loading…</Typography> : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <Chip label={`Original ${money(elig.original_amount)}`} />
              <Chip label={`Reversed ${money(elig.reversed_amount)}`} color={elig.reversed_amount > 0 ? "warning" : "default"} />
              <Chip label={`Remaining ${money(elig.remaining_refundable)}`} color={elig.remaining_refundable > 0 ? "primary" : "default"} />
              <Chip label={elig.derived_status} />
            </Stack>

            {!elig.reversible ? (
              <Alert severity="warning">
                This payment is not reversible ({elig.reason_if_not ?? "no eligible reason"}).
              </Alert>
            ) : (
              <>
                {wouldTryVoid ? (
                  <Alert severity="info">
                    This payment may be voided if Worldline still considers it unsettled. If it has passed the provider cutoff, the system will process it as a refund instead.
                  </Alert>
                ) : (
                  <Alert severity="info">
                    A partial amount is being reversed — the system will attempt a refund (voids are full-only).
                  </Alert>
                )}
                <TextField label="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={busy} />
                <TextField label="Reason" multiline minRows={2} value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} />
              </>
            )}

            <Divider>History</Divider>
            {(elig.history ?? []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">No prior reversals.</Typography>
            ) : (elig.history ?? []).map((h: any) => (
              <Typography key={h.id} variant="body2">
                {h.requested_at?.slice(0, 19).replace("T", " ")} · <strong>{h.kind}</strong> {money(h.amount)} — {h.status}
                {h.provider_outcome ? ` (${h.provider_outcome})` : ""}
                {h.reason ? ` — ${h.reason}` : ""}
              </Typography>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Close</Button>
        <Button variant="contained" color="error" onClick={submit} disabled={!canSubmit}>
          {busy ? "Processing…" : "Refund / Void"}
        </Button>
      </DialogActions>
      {busy && (
        <Box
          aria-live="polite" aria-busy="true"
          sx={{
            position: "absolute", inset: 0, zIndex: 10,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            gap: 1.5,
            bgcolor: "rgba(255,255,255,0.86)",
            backdropFilter: "blur(3px)",
            WebkitBackdropFilter: "blur(3px)",
          }}
        >
          <StepUpSpinner size="large" />
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary", mt: 1 }}>
            Processing refund…
          </Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            Please wait while we contact Worldline.
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

export default ReversePaymentDialog;
